import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatMoney, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Users, Package, ShoppingCart, ShieldCheck, DollarSign } from "lucide-react";

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const nav = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login?next=/admin"); return; }
    if (user.role !== "admin") { toast.error("Admin only"); nav("/dashboard"); return; }
    Promise.all([
      api.get("/admin/stats").then(r => setStats(r.data)),
      api.get("/admin/users").then(r => setUsers(r.data)),
      api.get("/admin/orders").then(r => setOrders(r.data)),
    ]).finally(() => setLoading(false));
  }, [user, authLoading, nav]);

  const confirmOrder = async (id) => {
    try {
      await api.post(`/orders/${id}/confirm`);
      toast.success("Order payment confirmed");
      const { data } = await api.get("/admin/orders");
      setOrders(data);
      const { data: s } = await api.get("/admin/stats");
      setStats(s);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (authLoading || loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#009A44]" size={28} /></div>;

  return (
    <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12" data-testid="admin-page">
      <div className="mb-8">
        <div className="text-[11px] font-bold uppercase tracking-widest text-[#EF3340] mb-2">Platform admin</div>
        <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">Control room</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
        <Stat icon={<Package size={18} />} label="Parts" value={stats.total_parts} />
        <Stat icon={<ShieldCheck size={18} />} label="Verified" value={stats.verified_parts} color="#009A44" />
        <Stat icon={<Users size={18} />} label="Users" value={stats.total_users} />
        <Stat icon={<ShoppingCart size={18} />} label="Orders" value={stats.total_orders} />
        <Stat icon={<ShoppingCart size={18} />} label="In hold" value={stats.pending_orders} color="#FFD100" />
        <Stat icon={<DollarSign size={18} />} label="Revenue" value={formatMoney(stats.revenue)} color="#121212" />
      </div>

      <div className="flex gap-1 mb-6 border-b border-[#E4E4E7]">
        {[
          { k: "overview", l: "Overview" },
          { k: "orders", l: `Orders (${orders.length})` },
          { k: "users", l: `Users (${users.length})` },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 ${tab === t.k ? "border-[#009A44] text-[#009A44]" : "border-transparent text-zinc-500 hover:text-[#121212]"}`}
            data-testid={`admin-tab-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-[#E4E4E7] p-6">
            <h3 className="font-display font-extrabold text-xl mb-3">Pending verifications</h3>
            <div className="font-display text-5xl font-extrabold text-[#FFD100]">{stats.pending_verifications}</div>
            <p className="text-sm text-zinc-500 mt-2">Parts awaiting local agent inspection.</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] p-6">
            <h3 className="font-display font-extrabold text-xl mb-3">Revenue</h3>
            <div className="font-display text-5xl font-extrabold text-[#009A44]">{formatMoney(stats.revenue)}</div>
            <p className="text-sm text-zinc-500 mt-2">Total from confirmed/shipped/delivered orders.</p>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="bg-white border border-[#E4E4E7] overflow-auto" data-testid="admin-orders">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-zinc-50 text-[11px] uppercase tracking-widest">
              <tr>
                <Th>Order</Th><Th>Buyer</Th><Th>Items</Th><Th>Total</Th><Th>Ship</Th><Th>Status</Th><Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.order_id} className="border-t border-[#E4E4E7]">
                  <Td className="font-mono text-xs">{o.order_id}</Td>
                  <Td>{o.buyer_email}</Td>
                  <Td>{o.items.length}</Td>
                  <Td className="font-semibold">{formatMoney(o.total)}</Td>
                  <Td className="uppercase text-xs">{o.shipping_method}</Td>
                  <Td><span className="text-xs font-bold uppercase">{o.status}</span></Td>
                  <Td>
                    {o.status === "payment_hold" && (
                      <button onClick={() => confirmOrder(o.order_id)} className="text-[#009A44] text-xs font-bold uppercase hover:underline" data-testid={`confirm-${o.order_id}`}>
                        Confirm
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "users" && (
        <div className="bg-white border border-[#E4E4E7] overflow-auto" data-testid="admin-users">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-zinc-50 text-[11px] uppercase tracking-widest">
              <tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Country</Th><Th>Provider</Th><Th>Joined</Th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-t border-[#E4E4E7]">
                  <Td>{u.name}</Td>
                  <Td>{u.email}</Td>
                  <Td><span className="text-xs font-bold uppercase text-[#009A44]">{u.role}</span></Td>
                  <Td>{u.country || "—"}</Td>
                  <Td className="uppercase text-xs text-zinc-500">{u.auth_provider || "password"}</Td>
                  <Td className="text-xs text-zinc-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}

function Stat({ icon, label, value, color = "#52525B" }) {
  return (
    <div className="bg-white border border-[#E4E4E7] p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color }}>
        {icon}{label}
      </div>
      <div className="font-display text-2xl font-extrabold truncate">{value}</div>
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-left font-bold">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
